/** @jsxImportSource @emotion/react */

import { useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useParams, createSearchParams } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import omit from 'lodash/omit'
import slugify from 'slugify'
import { PlatformSlug } from 'constants/consts'

import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { FindOneReward, FindOneRewardVariables } from '__generatedTypes__/FindOneReward'
import { UpdateReward, UpdateRewardVariables } from '__generatedTypes__/UpdateReward'
import { AwardTypeEnum, RewardTypeEnum } from '__generatedTypes__/globalTypes'
import { FIND_REWARD, UPDATE_REWARD } from 'queries/rewards'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import Loader from 'components/Loader'

import { toast } from 'utils/toast'
import * as classes from '../../styles'
import { AwardsTabs, QUERY_PARAM } from '../../consts'
import { getAwardId, getAwardTitle } from '../consts'
import { Form, FormValues } from './Form'
import { MAP_ENUM_TO_TITLE } from './consts'
import { getValue } from './utils'

type Props = {
  platform: Platform
}

export const RewardEdit = withPlatformCheck(({ platform }: Props) => {
  const { rewardId } = useParams()
  const { navigate } = useLink()

  const { data, loading: rewardLoading } = useQuery<FindOneReward, FindOneRewardVariables>(FIND_REWARD, {
    fetchPolicy: 'network-only',
    variables: {
      id: String(rewardId),
    },
    skip: !rewardId,
    onCompleted: async (data) => {
      const reward = data.findOneReward

      if (!reward) return
    },
  })

  const [updateReward] = useMutation<UpdateReward, UpdateRewardVariables>(UPDATE_REWARD)
  const handleSubmit = async (values: FormValues) => {
    if (!data?.findOneReward) return

    try {
      await updateReward({
        variables: {
          updateInput: {
            id: data.findOneReward.id,
            platform: { id: data.findOneReward.platform.id },

            type: values.type.id,
            percentage: Number(values.mysteryBoxPercentage),
            description: values.description,
            value: getValue(values),
          },
        },
      })

      navigate(cmsRoutes.ACHIEVEMENTS, undefined, {
        searchParams: createSearchParams({
          [QUERY_PARAM]: slugify(AwardsTabs.REWARDS, { lower: true }),
        }),
      })

      toast.success('Reward updated successfully')
    } catch (error) {
      toast.error('Failed to update reward')
    }
  }

  const initialValues = useMemo((): FormValues | undefined => {
    const reward = data?.findOneReward

    if (!reward) {
      return
    }

    const { type, value, ...rest } = reward

    return {
      ...omit(rest, '__typename', 'platform.__typename'),
      type: {
        id: type,
        title: MAP_ENUM_TO_TITLE[type],
      },
      extension:
        type === RewardTypeEnum.Extension
          ? {
              id: getAwardId(AwardTypeEnum.MembershipExtension, value),
              title: getAwardTitle(AwardTypeEnum.MembershipExtension, value),
            }
          : undefined,
      mysteryBoxPercentage: reward.percentage,
    }
  }, [data])

  if (rewardLoading) {
    return <Loader />
  }

  return (
    <section css={classes.container}>
      <Typography css={classes.title} variant="title3">
        Rewards
      </Typography>
      <Form initialValues={initialValues} onSubmit={handleSubmit} platform={platform.slug as PlatformSlug} />
    </section>
  )
})
